import React, { useState } from 'react';
import {
  Badge, Box, Button, Collapse, Flex, HStack, IconButton, Input, Popover, PopoverArrow,
  PopoverBody, PopoverContent, PopoverTrigger, Spacer, Stack, Text, Tooltip, useToast, useColorModeValue,
} from '@chakra-ui/react';
import {
  FiChevronDown, FiChevronUp, FiHeadphones, FiPlus, FiSlack, FiTrash2,
} from 'react-icons/fi';
import axios from 'axios';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useConfirm } from './ConfirmProvider';
import GlassCard from './GlassCard';
import ChatPanel from './ChatPanel';
import ChatFocusControls from './ChatFocusControls';
import SlackInvitePicker from './SlackInvitePicker';
import SlackChannelSelect from './SlackChannelSelect';
import AuroraBackground from './dashboard/AuroraBackground';
import PageHeader from './PageHeader';

const apiUrl = () => process.env.REACT_APP_BASIX_API || '';

// item 374 — project HELP DESKS: named support threads, each a full chat (the same
// append-only engine as project chat — threadKey helpdesk:{id}), built to BRIDGE to a
// Slack channel in the Pulse Group workspace (item 373). The Slack link is a working
// hook: naming a channel stores the intent now and auto-connects the moment the
// workspace credentials land (Daniel's setup).
interface HelpDesk {
  id: string;
  name: string;
  threadKey: string;
  slackChannelId: string | null;
  slackChannelName: string | null;
  desiredSlackChannelName: string | null;
  slackConfigured: boolean;
}

const useHelpDesks = (projectId: string) => useQuery<HelpDesk[]>(
  ['helpDesks', projectId],
  async () => {
    const { data } = await axios.get(`${apiUrl()}ProjectHelpDesk/project/${projectId}`);
    return Array.isArray(data) ? data : [];
  },
);

const SlackBadge: React.FC<{ desk: HelpDesk }> = ({ desk }) => {
  if (desk.slackChannelId) {
    return <Badge colorScheme="green" fontSize="0.6rem">{`SLACK #${desk.slackChannelName}`}</Badge>;
  }
  if (desk.desiredSlackChannelName) {
    return (
      <Tooltip hasArrow label="Channel reserved — connects automatically once the Slack workspace is wired up.">
        <Badge colorScheme="yellow" fontSize="0.6rem">{`SLACK PENDING #${desk.desiredSlackChannelName}`}</Badge>
      </Tooltip>
    );
  }
  return <Badge fontSize="0.6rem" variant="subtle">NOT ON SLACK</Badge>;
};

const HelpDeskCard: React.FC<{ desk: HelpDesk; projectId: string }> = ({ desk, projectId }) => {
  const confirm = useConfirm();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(true);
  const [channel, setChannel] = useState('');
  const [invitees, setInvitees] = useState<string[]>([]); // item 386
  const cardEdge = useColorModeValue('blackAlpha.100', 'whiteAlpha.200'); // item 958 — light-mode contrast
  // the effective channel: a fresh pick, else the already-linked / reserved one
  const effectiveChannel = channel || desk.slackChannelName || desk.desiredSlackChannelName || '';

  const linkSlack = useMutation(
    () => axios.post(`${apiUrl()}ProjectHelpDesk/${desk.id}/link-slack`, { channelName: effectiveChannel, inviteUserIds: invitees }),
    {
      onSuccess: (res) => {
        queryClient.invalidateQueries(['helpDesks', projectId]);
        const missed = (res.data?.noSlack ?? []) as string[];
        toast({
          status: res.data?.pending ? 'info' : 'success',
          title: res.data?.pending
            ? 'Channel reserved — links automatically once Slack is connected.'
            : `Linked to #${res.data?.channelName}${res.data?.invited ? ` — invited ${res.data.invited}` : ''}.`,
          description: missed.length > 0 ? `No Slack account found for: ${missed.join(', ')}` : undefined,
          duration: 7000,
          isClosable: true,
        });
      },
    },
  );

  const remove = useMutation(
    () => axios.delete(`${apiUrl()}ProjectHelpDesk/${desk.id}`),
    { onSuccess: () => queryClient.invalidateQueries(['helpDesks', projectId]) },
  );

  return (
    <Box borderWidth="1px" borderColor={cardEdge} borderRadius="lg" p={3} mb={3}>
      <Flex align="center" sx={{ gap: '8px' }}>
        <IconButton
          aria-label={open ? 'Collapse help desk' : 'Expand help desk'}
          icon={open ? <FiChevronUp /> : <FiChevronDown />}
          size="xs"
          variant="ghost"
          onClick={() => setOpen((o) => !o)}
        />
        <FiHeadphones />
        <Text fontWeight="bold" fontSize="sm">{desk.name}</Text>
        <Spacer />
        {/* item 400c — channel badge lives in the right cluster, same spot as Project Talk */}
        <SlackBadge desk={desk} />
        <Popover placement="bottom-end">
          <PopoverTrigger>
            <Button size="xs" variant="outline" leftIcon={<FiSlack />}>
              {desk.slackChannelId ? 'Slack settings' : 'Link to Slack'}
            </Button>
          </PopoverTrigger>
          <PopoverContent w="300px">
            <PopoverArrow />
            <PopoverBody>
              <Text fontSize="xs" mb={2} opacity={0.75}>
                {desk.slackConfigured
                  ? 'Pick the public Slack channel to link. (Create a new channel in Slack first if you need one.)'
                  : 'Slack isn’t connected to Basix yet — channels will list once it is.'}
              </Text>
              <Stack spacing={2}>
                {/* item 403c — choose an existing public channel (no private channels) */}
                <SlackChannelSelect
                  value={effectiveChannel}
                  onChange={setChannel}
                  initialFilter={desk.desiredSlackChannelName ?? desk.name}
                />
                {/* item 386 — invite teammates while linking */}
                <SlackInvitePicker selected={invitees} onChange={setInvitees} />
                <Button
                  size="sm"
                  isLoading={linkSlack.isLoading}
                  isDisabled={!effectiveChannel}
                  onClick={() => linkSlack.mutate()}
                >
                  Save
                </Button>
              </Stack>
            </PopoverBody>
          </PopoverContent>
        </Popover>
        <ChatFocusControls threadKey={desk.threadKey} title={`Help Desk — ${desk.name}`} />
        <IconButton
          aria-label="Archive help desk"
          icon={<FiTrash2 />}
          size="xs"
          variant="ghost"
          colorScheme="red"
          onClick={async () => {
            if (await confirm(`Archive the "${desk.name}" help desk? Its chat history is kept.`, { title: 'Archive help desk', confirmLabel: 'Archive', destructive: true })) remove.mutate();
          }}
        />
      </Flex>
      <Collapse in={open} animateOpacity>
        <Box mt={3}>
          <ChatPanel threadKey={desk.threadKey} />
        </Box>
      </Collapse>
    </Box>
  );
};

const ProjectHelpDesksSection: React.FC<{
  projectId: string;
  collapse?: { collapsed: boolean; onToggle: () => void };
}> = ({ projectId, collapse }) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { data: desks } = useHelpDesks(projectId);
  const [draft, setDraft] = useState('');

  const create = useMutation(
    () => axios.post(`${apiUrl()}ProjectHelpDesk`, { projectId, name: draft.trim() }),
    {
      onSuccess: () => {
        setDraft('');
        queryClient.invalidateQueries(['helpDesks', projectId]);
      },
      onError: () => { toast({ status: 'error', title: 'Could not add the help desk' }); },
    },
  );

  const rows = desks ?? [];
  const open = !(collapse?.collapsed ?? false);

  return (
    // item 381 — same section language as Documents/Talk: bleed panel + compact header
    <Box
      position="relative"
      mx={{ base: -4, md: -10 }}
      px={{ base: 4, md: 10 }}
      pt={open ? { base: 2, md: 3 } : 1}
      pb={open ? { base: 2, md: 3 } : 1}
    >
      <AuroraBackground />
      <Box position="relative" zIndex={1}>
        <PageHeader
          dense
          onTitleClick={collapse?.onToggle}
          collapsed={!open}
          icon={<FiHeadphones />}
          title="Help Desks"
          subtitle={`${rows.length} ${rows.length === 1 ? 'desk' : 'desks'} · Slack-bridged support threads`}
        />
        <Collapse in={open} animateOpacity>
          <GlassCard p={4}>
            {rows.map((d) => <HelpDeskCard key={d.id} desk={d} projectId={projectId} />)}
            <HStack mt={rows.length > 0 ? 1 : 0}>
              <Input
                size="sm"
                placeholder="Help desk name"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && draft.trim()) create.mutate(); }}
              />
              {/* item 1183 dart#7 (Chad, mobile) — hold shape: the input squeezed the
                  button narrower than its label */}
              <Button
                size="sm"
                flexShrink={0}
                leftIcon={<FiPlus />}
                isDisabled={!draft.trim()}
                isLoading={create.isLoading}
                onClick={() => create.mutate()}
              >
                Add Help Desk
              </Button>
            </HStack>
          </GlassCard>
        </Collapse>
      </Box>
    </Box>
  );
};

ProjectHelpDesksSection.defaultProps = { collapse: undefined };

export default ProjectHelpDesksSection;
